/**
 * Custom hook to handle address autocomplete functionality.
 *
 * @param {Object} params - The parameters for the hook.
 * @param {Object} params.formContext - The form context to set form values.
 * @param {string} params.name - The name of the form field.
 * @param {Object} params.uiOptions - UI options for the autocomplete.
 * @param {boolean} params.isAddress - Flag to indicate if the field is an address.
 *
 * @returns {Object} - The state and handlers for address autocomplete.
 * @returns {boolean} isVisible - State to control the visibility of the autocomplete dropdown.
 * @returns {Function} setIsVisible - Function to set the visibility of the autocomplete dropdown.
 * @returns {Object} dropdownPosition - The position of the autocomplete dropdown.
 * @returns {Function} setDropdownPosition - Function to set the position of the autocomplete dropdown.
 * @returns {Array} predictionResults - The prediction results from Google Places API.
 * @returns {Function} handleSuggestionClick - Handler for suggestion click event.
 * @returns {Function} setAddressInputValue - Function to set the address input value.
 * @returns {Object|null} formattedAddress - The formatted address object.
 *
 * @example
 * const {
 *   isVisible,
 *   setIsVisible,
 *   dropdownPosition,
 *   setDropdownPosition,
 *   predictionResults,
 *   handleSuggestionClick,
 *   setAddressInputValue,
 *   formattedAddress,
 * } = useAddressAutocomplete({
 *   formContext,
 *   name: 'address',
 *   uiOptions: { autoCompleteAddress: [{ fieldName: 'street', type: 'street' }] },
 *   isAddress: true,
 * })
 */
import { COUNTRIES } from '@/utils/constants'
import { useEffect, useState } from 'react'
import { useGooglePlaces } from './useGooglePlaces'

export const useAddressAutocomplete = ({
  formContext,
  name,
  uiOptions,
  isAddress,
}) => {
  const [isVisible, setIsVisible] = useState(false)
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 })

  const {
    setInputValue: setAddressInputValue,
    formattedAddress,
    predictionResults,
    handleSuggestionClick,
  } = useGooglePlaces()

  const isEditFieldName = name.includes('edit')
  const baseFieldName = isEditFieldName ? name.replace(/-edit$/, '') : name
  const toFormFieldName = (fieldName) =>
    `${fieldName}${isEditFieldName ? '-edit' : ''}`
  const autoCompleteAddress = uiOptions?.autoCompleteAddress ?? []
  const autoCompleteAddressSignature = autoCompleteAddress
    .map(({ fieldName, type }) => `${fieldName}:${type}`)
    .join('|')
  const progressiveDisclosureField = uiOptions?.progressiveDisclosureField
    ? toFormFieldName(uiOptions.progressiveDisclosureField)
    : null

  const setFormValueIfChanged = (fieldName, value) => {
    if (!formContext) return
    const currentValue = formContext.getValues(fieldName)
    if (currentValue !== value) {
      formContext.setValue(fieldName, value)
    }
  }

  const getAddressFieldsToCheck = () => [
    toFormFieldName(baseFieldName),
    ...autoCompleteAddress
      .filter(
        ({ fieldName, type }) =>
          fieldName !== baseFieldName && type !== 'country'
      )
      .map(({ fieldName }) => toFormFieldName(fieldName)),
  ]

  const hasAnyAddressValue = (fieldsToCheck) =>
    fieldsToCheck.some((fieldName) => {
      const rhfValue = formContext.getValues(fieldName)
      if (typeof rhfValue === 'string' && rhfValue.trim() !== '') return true
      const domInput = document.querySelector(`input[name="${fieldName}"]`)
      return domInput?.value && domInput.value.trim() !== ''
    })

  const syncDomValuesToRhf = (fieldsToCheck) => {
    fieldsToCheck.forEach((fieldName) => {
      const domInput = document.querySelector(`input[name="${fieldName}"]`)
      if (!domInput?.value) return
      const rhfValue = formContext.getValues(fieldName)
      if (domInput.value !== rhfValue) {
        formContext.setValue(fieldName, domInput.value, { shouldDirty: true })
      }
    })
  }

  const handleAddressBlurFallback = () => {
    if (!isAddress || !formContext || !progressiveDisclosureField) return

    const isProgressiveDisclosureOpen =
      formContext.getValues(progressiveDisclosureField) === true
    if (isProgressiveDisclosureOpen) return

    const fieldsToCheck = getAddressFieldsToCheck()

    if (hasAnyAddressValue(fieldsToCheck)) {
      syncDomValuesToRhf(fieldsToCheck)
      setFormValueIfChanged(progressiveDisclosureField, true)
    }
  }

  const getCountryCode = (country) => {
    const foundCountry = COUNTRIES.find(
      (c) => c.label.toLowerCase() === country.toLowerCase()
    )
    return foundCountry?.value ?? 'IT'
  }

  const getAddressFieldValue = (address, type) => {
    switch (type) {
      case 'street':
        return address.street
      case 'streetNumber':
        return address.streetNumber
      case 'postalCode':
        return address.postalCode
      case 'city':
        return address.city
      case 'state':
        return address.state
      case 'province':
        return address.province
      case 'country':
        return address.country === 'Italia'
          ? 'IT'
          : getCountryCode(address.country)
      default:
        return ''
    }
  }

  useEffect(() => {
    if (
      isAddress &&
      formattedAddress !== null &&
      formContext &&
      autoCompleteAddress.length > 0
    ) {
      autoCompleteAddress.forEach(({ fieldName: _fieldName, type }) => {
        const fieldName = toFormFieldName(_fieldName)
        const value = getAddressFieldValue(formattedAddress, type)
        setFormValueIfChanged(fieldName, value)
      })

      if (progressiveDisclosureField) {
        setFormValueIfChanged(progressiveDisclosureField, true)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    formattedAddress,
    isAddress,
    name,
    progressiveDisclosureField,
    autoCompleteAddressSignature,
  ])

  useEffect(() => {
    if (!isAddress || !formContext || !progressiveDisclosureField) return

    const fieldsToCheck = getAddressFieldsToCheck()

    if (hasAnyAddressValue(fieldsToCheck)) {
      syncDomValuesToRhf(fieldsToCheck)
      setFormValueIfChanged(progressiveDisclosureField, true)
    }

    const addressFieldName = toFormFieldName(baseFieldName)

    const subscription = formContext.watch(
      (formValues, { name: changedField }) => {
        if (!fieldsToCheck.includes(changedField)) return

        const isDisclosureOpen =
          formContext.getValues(progressiveDisclosureField) === true
        if (isDisclosureOpen) return

        const addressInput = document.querySelector(
          `input[name="${addressFieldName}"]`
        )
        if (addressInput && document.activeElement === addressInput) return

        const hasValue = fieldsToCheck.some((fn) => {
          const val = formValues[fn]
          return typeof val === 'string' && val.trim() !== ''
        })

        if (hasValue) {
          setFormValueIfChanged(progressiveDisclosureField, true)
        }
      }
    )

    return () => subscription.unsubscribe()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isAddress,
    progressiveDisclosureField,
    name,
    autoCompleteAddressSignature,
  ])

  return {
    isVisible,
    setIsVisible,
    dropdownPosition,
    setDropdownPosition,
    predictionResults,
    handleSuggestionClick,
    setAddressInputValue,
    handleAddressBlurFallback,
    formattedAddress,
  }
}
